import { Component, Input, OnInit } from '@angular/core';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, map } from 'rxjs/operators';

import { Association } from '../../../../../../../models/association';
import { CallbackResult, dateStringFromISO8601 } from '../../../../../../../utils';
import { ListResponse } from '../../../../../../../models/list-response';
import { AccountService } from '../../../../../../../services/account/account.service';
import {
  CustomerEntryPass,
  CustomerEntryPassUnlinkedCustomer
} from '../../../../../../../models/entry-pass';
import { EntryPassService } from '../../../../../../../services/entry-pass/entry-pass.service';
import { CreateCustomerEntryPassModalComponent } from '../../../../customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/create-customer-entry-pass-modal/create-customer-entry-pass-modal.component';
import { ReviewUnlinkedEntryPassCustomerModalComponent } from '../../../../inbox/inbox-memberships-and-passes/inbox-unlinked-entry-passes/modals/review-unlinked-entry-pass-customer-modal/review-unlinked-entry-pass-customer-modal.component';
import { InboxService } from '../../../../../../../services/inbox/inbox.service';
import { EditCustomerEntryPassModalComponent } from '../../../../customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-customer-entry-pass-modal/edit-customer-entry-pass-modal.component';
import { LinkCustomerEntryPassModalComponent } from '../../../../customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/link-customer-entry-pass-modal/link-customer-entry-pass-modal.component';
import { PurchaseCustomerEntryPassModalComponent } from '../../../../customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/purchase-customer-entry-pass-modal/purchase-customer-entry-pass-modal.component';
import { CustomerEntryPassStatus } from '../../../../../../../models/entry-pass-enums';


@Component({
  selector: 'app-association-entry-passes-sub-list',
  templateUrl: './association-entry-passes-sub-list.component.html',
  styleUrls: ['./association-entry-passes-sub-list.component.css']
})
export class AssociationEntryPassesSubListComponent implements OnInit {
  @Input() association: Association;
  @Input() resultCallback: (result: CallbackResult) => void;

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeAssociationEntryPassesSubList;

  public filtersChangedSubject = new Subject<null>();

  public passList: ListResponse<CustomerEntryPass>;

  public loadingPasses = true;
  public loadingRemove = false;
  public loadingUnlinkedEntryPassCustomer = {};

  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public CustomerEntryPassStatus = CustomerEntryPassStatus;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., pagination).
  private listRequestCount = 0;

  constructor(
    private accountService: AccountService,
    private entryPassService: EntryPassService,
    private inboxService: InboxService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    if (!this.pageSizeAssociationEntryPassesSubList) {
      this.pageSizeAssociationEntryPassesSubList = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeAssociationEntryPassesSubList = this.pageSizeAssociationEntryPassesSubList;

    // Handle changes to the filters by reloading the members,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => { this.loadingPasses = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadPasses();
    });

    this.loadPasses();
  }

  public openAddPassModal() {
    const modalRef = this.modalService.open(CreateCustomerEntryPassModalComponent);
    modalRef.componentInstance.association = this.association;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadPasses();
    };
  }

  public openPurchasePassModal() {
    const modalRef = this.modalService.open(PurchaseCustomerEntryPassModalComponent);
    modalRef.componentInstance.association = this.association;
  }

  public editEntryPass(entryPass: CustomerEntryPass) {
    const editModalRef = this.modalService.open(
      EditCustomerEntryPassModalComponent
    );
    editModalRef.componentInstance.entryPass = entryPass;
    editModalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadPasses();
    };
  }

  public linkEntryPass(entryPass: CustomerEntryPass) {
    const editModalRef = this.modalService.open(
      LinkCustomerEntryPassModalComponent
    );
    editModalRef.componentInstance.entryPass = entryPass;
    editModalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(result);
      this.loadPasses();
    };
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.passList) { return 1; }
    return Math.ceil(this.passList.count / this.pageSizeAssociationEntryPassesSubList);
  }

  public openLinkUnlinkedEntryPassCustomerModal(unlinkedCustomer: CustomerEntryPassUnlinkedCustomer) {
    if (this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id]) { return; }

    this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id] = true;

    this.inboxService
      .getUnlinkedEntryPassCustomer(unlinkedCustomer.id)
      .then(unlinkedCustomerInboxItem => {
        const modalRef = this.modalService.open(ReviewUnlinkedEntryPassCustomerModalComponent);
        modalRef.componentInstance.unlinkedCustomerInboxItem = unlinkedCustomerInboxItem;
        modalRef.componentInstance.savedCallback = () => {
          this.loadPasses();

          // Set the loading state back to true - will be reset when the entry passes reload.
          this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id] = true;
        };
      })
      .catch(reason => {
        if (reason.status === 404) {
          // 404 indicates that the entry pass is no longer unlinked, so reload.
          this.loadPasses();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id] = false;
      });
  }

  private loadPasses() {
    this.loadingPasses = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.entryPassService
      .getCustomerEntryPasses(
        this.pageNumber,
        this.pageSizeAssociationEntryPassesSubList,
        {
          owner_association_id: this.association.id
        }
      )
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.passList = response;
          this.loadingUnlinkedEntryPassCustomer = {};
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingPasses = false;
        }
      });
  }
}
